import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { useRecoilState, useRecoilValue } from 'recoil'

import { workState } from '../../store'
import { useCart } from '../../utils/useCart'
import Inner from '../../components/Inner'
import Title from '../../components/Title'
import { workApi } from '../../api'
import Button from '../../components/Button'
import PhotoGallery from './PhotoGallery'
import { selectedCurrencyState } from '../../store/selectedCurrencyAtom'
import { useConvertPrice } from '../../utils/convertPrice'
import { displayPrice } from '../../utils/displayPrice'
import { VideoPlayer } from '../collections/VideoPlayer'
import ShippingCost from '../../components/ShippingCost'
import Artplacer from '../../components/Artplacer'
import { routes } from '../../routes'
import OfferForm from '../../components/OfferForm'
import axios from 'axios'
import { apiURL } from '../../config'
import { Helmet } from 'react-helmet-async'

function ShowWork() {
    const { t } = useTranslation()
    const { slug } = useParams()

    const navigate = useNavigate()

    const [work, setWork] = useRecoilState(workState)
    const [shippingInfo, setShippingInfo] = useState(null);

    const selectedCurrency = useRecoilValue(selectedCurrencyState)
    const convertPrice = useConvertPrice()

    const [offerModalOpen, setOfferModalOpen] = useState(false);
    const [notification, setNotification] = useState(null);
    const notificationTimeout = useRef(null);

    const handleOfferSubmit = async ({ email, price, currency }) => {
        try {
            await axios.post(`${apiURL}/price-offer/`, {
                painting_id: work.id,
                email,
                price,
                currency
            })
            setOfferModalOpen(false);
            setNotification({ type: 'success', message: 'Your offer has been sent!' });
        } catch (e) {
            setOfferModalOpen(false);
            let errorMsg = 'Failed to send offer. Please try again later.';
            if (e.response && e.response.data && e.response.data.detail) {
                errorMsg = e.response.data.detail;
            }
            setNotification({ type: 'error', message: errorMsg });
        }
        clearTimeout(notificationTimeout.current);
        notificationTimeout.current = setTimeout(() => setNotification(null), 3000);
    };

    const { cart, addToCart, removeFromCart } = useCart()

    const packageDetails = useMemo(() => ({
        weight: work?.weight || 5,
        length: work?.length || 8,
        width: work?.width || 50,
        height: work?.height || 50,
    }), [work?.weight, work?.length, work?.width, work?.height]);

    // Check if the current item is already in the cart
    const isInCart = cart.some((item) => item.id === work?.id)

    const getWorkSize = useCallback((work) => {
        // Collect dimensions as [value, label] pairs for clarity if needed
        const dims = [
            work?.length,
            work?.height,
            work?.width
        ].filter(Boolean);

        if (dims.length === 3) {
            // Show only the two largest
            const sorted = [...dims].sort((a, b) => b - a);
            return sorted.slice(0, 2).join('x');
        }
        // Show all available if less than 3
        return dims.join('x');
    }, []);

    const getWork = useCallback(async (workSlug) => {
        if (!workSlug) return
        setWork(null)
        const response = await workApi.show(workSlug)
        setWork(response)
    }, [])

    useEffect(() => {
        if (work && work.is_shown === false) {
            navigate(routes.error.notFound, { replace: true })
        }
    }, [work, navigate])

    useEffect(() => {
        if (!slug) return
        getWork(slug)
    }, [slug, getWork])

    return (
        <>
            <Helmet>
                <title>Works | Eduard Belsky Art Gallery</title>
                <meta name="description" content="Explore all available paintings and ceramic works by Eduard Belsky. Find your next piece of art." />
                <link rel="canonical" href="https://eduardbelsky.com/works" />
                <meta name="robots" content="index, follow" />
                <meta property="og:title" content="Works | Eduard Belsky Art Gallery" />
                <meta property="og:description" content="Explore all available paintings and ceramic works by Eduard Belsky. Find your next piece of art." />
                {/* <meta property="og:image" content="https://eduardbelsky.com/images/og-works.jpg" /> */}
                <meta property="og:image" content="%PUBLIC_URL%/logo192.png" />
                <meta property="og:url" content="https://eduardbelsky.com/works" />
            </Helmet>
            <h1 className="sr-only">{work?.title || "Artwork by Eduard Belsky"}</h1>
            {/* Notification for Make an Offer button*/}
            {notification && (
                <div
                    className={`fixed bottom-6 right-6 z-[9999] px-9 py-6 rounded shadow-lg transition 
                        ${notification.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}
                >
                    {notification.message}
                </div>
            )}
            {offerModalOpen && (
                <OfferForm
                    onSubmit={({ email, price }) =>
                        handleOfferSubmit({ email, price, currency: selectedCurrency })
                    }
                    onClose={() => setOfferModalOpen(false)}
                    selectedCurrency={selectedCurrency}
                />
            )}

            <Title>{work?.title}</Title>
            <Inner>
                <h1 className="text-5xl py-6">{work?.title}</h1>
                <div className="flex flex-col sm:flex-row gap-6 sm:pb-16">
                    <div className="sm:w-1/2">
                        <PhotoGallery
                            items={work?.painting_urls
                                .filter((subList) => subList.length > 1)
                                .map((subList) => subList[2])}
                        />
                    </div>
                    <div className="sm:w-1/2">
                        <div className="flex flex-col py-4 sm:px-12 sm:py-0 gap-4">
                            <div className="text-lg sm:text-base font-light">
                                {work?.year && <div>{t('Year of painting')}: {work?.year}</div>}
                                {work?.collection && <div>{t('Collection')}: {work?.collection?.title}</div>}
                                {work?.medium && <div>{t('Medium')}: {work?.medium}</div>}
                                {(work?.height || work?.length || work?.width) && <div>{t('Size')}: {getWorkSize(work)} cm</div>}
                                {work?.video_url && (
                                    <VideoPlayer
                                        className="w-full aspect-video my-4"
                                        src={`${work?.video_url}?autoplay=1`}
                                    />
                                )}
                            </div>
                            <p className="text-xl">
                                {work?.description}
                            </p>
                            {(!work?.collection?.title || work.collection.title.toLowerCase() !== "ceramic") && (
                                <Artplacer
                                    artworkUrl={work?.painting_urls?.[0]?.[2]}
                                    height={work?.height}
                                    title={work?.title}
                                    size={getWorkSize(work)}
                                    price={work?.price_eur}
                                    artist="Eduard Belsky"
                                />
                            )}
                            <p className='mb-4'>
                                {work?.price_eur && (
                                    <span className="text-3xl sm:text-3xl font-normal">
                                        {displayPrice(work?.price_eur, work?.discount, convertPrice, selectedCurrency)}
                                    </span>
                                )}
                            </p>
                            {work && (
                                <ShippingCost
                                    key={`${work.id}`} // Force re-render when data changes
                                    packageDetails={packageDetails}
                                    onShippingCalculated={setShippingInfo}
                                />)}
                            {work?.is_sold ? (
                                <Button
                                    type="primary"
                                    title={t('Sold')}
                                    styles="uppercase text-xl sm:text-sm"
                                    disabled
                                />
                            ) : isInCart ? (
                                <Button
                                    type="secondary"
                                    title={t('Remove from Cart')}
                                    styles="uppercase text-xl sm:text-sm bg-white text-black border border-black hover:bg-black hover:text-white transition"
                                    onClick={() => removeFromCart(work.id)}
                                />
                            ) : (
                                <>
                                    <Button
                                        type="primary"
                                        title={t('Add to Cart')}
                                        styles="uppercase text-xl sm:text-sm bg-black text-white hover:bg-gray-800 transition"
                                        onClick={() => addToCart({ ...work, shippingInfo })}
                                    />
                                    <Button
                                        type="secondary"
                                        title={t('Make an Offer')}
                                        styles="uppercase text-xl sm:text-sm ml-2 bg-white text-black border border-black hover:bg-black hover:text-white transition"
                                        onClick={() => setOfferModalOpen(true)}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </Inner>
            <script type="application/ld+json">
                {`
                {
                "@context": "https://schema.org",
                "@type": "Product",
                "name": "${work?.title}",
                "image": "${work?.painting_urls?.[0]?.[2]}",
                "description": "${work?.description}",
                "brand": {
                    "@type": "Person",
                    "name": "Eduard Belsky"
                },
                "offers": {
                    "@type": "Offer",
                    "price": "${work?.price_eur}",
                    "priceCurrency": "EUR",
                    "availability": "https://schema.org/InStock",
                    "url": "https://eduardbelsky.com/works/${work?.slug}"
                }
                }
                `}
            </script>
        </>
    );
}

export default ShowWork;